import {
  includesBuildings,
  includesContents,
  getDirectOrDeepValue,
} from "@/common/answers";
import { adStringToBool } from "@/common/cover";
import { filterMultiQuoteDocs, filterSingleQuoteDocs } from "@/common/docs";
import { logErrorsToGoogleAnalytics } from "@/common/log-errors";
import { shouldGoToStart } from "@/common/navigating";
import {
  getProductId,
  getQuestionSetData,
  isQuilterSpecific,
} from "@/common/question-set";
import {
  quoteResponseToQuoteAccessId,
  quoteResponseToState,
  isQuoteExpired,
  hasConfirmedEligibility,
} from "@/common/quotes";
import { isEmpty } from "@/common/utils";
import { ANSWER_SOURCES } from "@/constants/answer.constants";

import {
  APPLICATION_STEPS,
  CALL_CHANNEL,
  CLEAN,
  CLIENTERROR,
  D2C_COM_TYPE,
  DEFAULTED,
  EXIT_DESTINATION,
  FAILED,
  FIRST_TIME_BUYER_NCD,
  INPROGRESS,
  PREQUOTE,
  PROCEEDED,
  PRODUCT,
  QUICKQUOTERETRIEVED,
  QUOTESRETRIEVED,
  STEP_TITLE,
  SUBMITTED,
  UNABLETOQUOTE,
} from "@/constants/common.constants";
import {
  CUSTOMISE_COVER_INTERFACE_KEYS,
  PROPERTY_DETAILS_INTERFACE_KEY,
} from "@/constants/form.constants";
import { SYSTEM_ERRORS } from "@/constants/message.constants";
import { PROPERTY_LOOKUP } from "@/constants/property-detail.constant";
import { ROUTE_NAME } from "@/constants/route.constants";
import router from "@/router";
import appConfigurationService from "@/services/app-configuration.service";
import catalogueService from "@/services/catalogue.service";
import documentService from "@/services/document.service";
import quoteService from "@/services/quote.service";
import securityService from "@/services/security.service";

export default {
  createQuote({ commit, dispatch, getters }) {
    const quoteRequest = getters.getQuoteRequest;
    const config = getters.httpConfig;
    commit("SET_QUOTE_STATUS", PREQUOTE);
    return quoteService.createQuote(quoteRequest, config).then((data) => {
      dispatch("updateQuoteResponse", data);
    });
  },
  getQuotes({ dispatch, commit }, quoteRequestFields) {
    // Clear previous error flags first
    commit("RESET_LAST_QUOTE_ERROR");
    // Update and hopefully get prices for the quoteRequest
    return dispatch("updateQuote", quoteRequestFields);
  },
  updateQuote({ state, commit, dispatch, getters }, quoteRequestFields) {
    // POST an update to an existing QuoteRequestId

    // Check for a replacement product if an expired quote is being updated.
    if (getters.isExpired && getters.replacementProductAvailable) {
      dispatch("setReplacementProductId");
    }

    // If the quote is already priced, API returns a new clone instead
    // We patch in the quoteRequestFields to set the IsPartialQuote flag (defaults to true if unset)
    const quoteRequest = {
      UserReference: state.userReference,
      ...getters.getQuoteRequest,
      IsPartialQuote: quoteRequestFields.IsPartialQuote,
    };
    const config = getters.httpConfig;
    const qrid = getters.getQuoteRequestId;

    return quoteService
      .updateQuote(qrid, quoteRequest, config)
      .then((data) => {
        dispatch("updateQuoteResponse", data);
        return data;
      })
      .catch((error) => {
        if (error.response?.data?.StatusCode === CLIENTERROR) {
          dispatch("updateClientErrorResponse", error.response.data);
        } else {
          commit("SET_QUOTE_STATUS", FAILED);
        }
        throw error; // Rethrow for downstream handlers
      });
  },
  saveQuoteResults({ state, commit, getters }) {
    // POST to new API Endpoint to Save the Results data
    const adSelections = getters.coverSelection;
    const saveQuoteResults = {
      CommissionSacrifice: state.commissionSacrifice,
      UserReference: state.userReference,
      BuildingsAD: adSelections.buildingsAd,
      ContentsAD: adSelections.contentsAd,
      CallChannelType: getters.callChannel,
    };

    if (state.selectedQuoteIds?.length) {
      saveQuoteResults.QuotesForComparison = state.selectedQuoteIds;
    }

    if (getters.isSelfServe) {
      saveQuoteResults.ActiveQuoteId = state.quoteResponse.ActiveQuoteId;
      saveQuoteResults.Quotes = state.quoteResponse.Quotes;
      saveQuoteResults.Prices = state.quoteResponse.Prices;
    }

    const config = getters.httpConfig;
    const qrid = getters.getQuoteRequestId;

    return quoteService
      .saveQuoteResults(qrid, saveQuoteResults, config)
      .then((data) => {
        return data;
      })
      .catch((error) => {
        commit("SET_QUOTE_STATUS", FAILED);
        throw error; // Rethrow for downstream handlers
      });
  },
  cloneQuote({ dispatch }) {
    // Clone the existing, priced quote to make a new partial one
    const quoteRequestFields = {
      IsPartialQuote: true,
    };
    dispatch("updateQuote", quoteRequestFields);
  },
  async refreshSession({ getters, state }) {
    if (state.quoteAccessId) {
      //D2C anonymous user doesn't need refresh session PSL Api
      return;
    }
    const config = getters.httpConfig;
    try {
      await securityService.refresh(config);
      return true;
    } catch (error) {
      console.log({ error });
      if (error && error.response && error.response.status === 401)
        throw new Error(SYSTEM_ERRORS.errorSessionExpired.errorId);
      return false;
    }
  },
  setCredentials({ state, commit }, { userId, token, quoteAccessId }) {
    // Set the quote credentials based on URL
    // Prefer the URL userId+token, (now we have quoteAccessId for D2C)
    // But if absent, use the ones from state/localStorage
    // Reset other type of credentials so that they don't mix with each other
    if (quoteAccessId) {
      userId = null;
      token = null;
    } else if (userId && token) {
      quoteAccessId = null;
    } else if (state.quoteAccessId) {
      quoteAccessId = state.quoteAccessId;
      userId = null;
      token = null;
    } else if (state.userId && state.token) {
      userId = state.userId;
      token = state.token;
      quoteAccessId = null;
    }

    // Still got nothing, stop execution
    if ((!userId || !token) && !quoteAccessId) return;

    commit("UPDATE_CREDENTIALS", {
      UserId: userId,
      Token: token,
      QuoteAccessId: quoteAccessId,
    });
  },
  setBranchChannelAndQsid(
    { commit },
    { branchNumber, callChannel, questionSetId }
  ) {
    if (branchNumber) commit("UPDATE_BRANCH", branchNumber);
    if (callChannel) commit("UPDATE_CALLCHANNEL", callChannel);
    if (questionSetId) {
      commit("UPDATE_QSID", questionSetId);
      const productId = getProductId(questionSetId);
      if (!productId) {
        throw new Error(SYSTEM_ERRORS.errorProductNotFound.errorId);
      }
      commit("UPDATE_PRODUCT_ID", productId);
    }
  },
  async loadQuestionsSections({ commit, dispatch, getters }, journey = "home") {
    try {
      const qsData = await getQuestionSetData(
        getters.qsId,
        getters.branchNumber,
        getters.callChannel,
        "questions",
        journey
      );
      commit("SET_SECTIONS", qsData);
      dispatch("setQuestionMap");
      dispatch("steps/initializeQuotationSteps", qsData.Sections, {
        root: true,
      });
    } catch (error) {
      console.error(error);
      throw new Error(SYSTEM_ERRORS.errorRedirectAdviserHub.errorId);
    }
  },
  async getCatalogueData({ dispatch, getters }) {
    if (getters.hasCatalogue) return;
    return await Promise.all([
      dispatch("getOccupations"),
      dispatch("getIndustries"),
    ]);
  },
  async getIndustries({ commit, getters }) {
    const config = getters.httpConfig;
    try {
      const data = await catalogueService.industry(config);
      commit("SET_INDUSTRIES", data);
    } catch (error) {
      return console.log(error);
    }
  },
  async getOccupations({ commit, getters }) {
    const config = getters.httpConfig;
    try {
      const data = await catalogueService.occupation(config);
      commit("SET_OCCUPATIONS", data);
    } catch (error) {
      return console.log(error);
    }
  },
  async getProducts({ commit, getters }) {
    const config = getters.httpConfig;
    try {
      const data = await catalogueService.product(config);
      commit("SET_PRODUCTS", data);
    } catch (error) {
      console.log(error);
      commit("SET_PRODUCTS", []);
    }
  },
  async getCapabilities({ commit, getters }) {
    const config = getters.httpConfig;
    try {
      const data = await catalogueService.system(config);
      commit("SET_CAPABILITIES", data.Capabilities);
    } catch (error) {
      return console.log(error);
    }
  },
  async initQuoteAndBuy({ state, dispatch }, urlParams) {
    if (!urlParams) {
      console.error("You must pass a urlParams object to initQuoteAndBuy");
      return;
    }

    // Updates these variables if they were found in the querystring
    await dispatch("setCredentials", urlParams);
    await dispatch("setBranchChannelAndQsid", urlParams);

    // Fetch occupation/industry data if there is none
    dispatch("getCatalogueData");
    await dispatch("getProducts");

    // Fetch quote excess configuration
    await dispatch("fetchQuoteExcessConfiguration");

    // Check whether this is a 'create quote', or 'edit quote' journey
    if (urlParams.quoteRequestId || urlParams.quoteAccessId) {
      // 'Edit quote' journey
      if (urlParams.quoteAccessId) {
        await dispatch("loadQuoteByAccessId", urlParams.quoteAccessId).catch(
          (error) => {
            console.error(error);
            throw new Error(SYSTEM_ERRORS.errorRedirectHomepage.errorId);
          }
        );
      } else {
        await dispatch("loadQuote", urlParams.quoteRequestId).catch((error) => {
          console.error(error);
          throw new Error(SYSTEM_ERRORS.errorRedirectAdviserHub.errorId);
        });
      }

      // Check whether user canSelectForComparison
      await dispatch("getCapabilities");

      // Now we have QuestionSetId, BranchNumber, and CallChannel type
      // Await the questionset so we can restore the answers into it
      await Promise.all([
        dispatch("loadQuestionsSections"),
        dispatch("application/loadApplicationSections", null, {
          root: true,
        }),
      ]);

      // Check whether an exired quote is being requoted.
      let isExpired = false;
      if (state.quoteResponse.QuoteStatus !== SUBMITTED) {
        if (urlParams.isExpired) {
          isExpired = urlParams.isExpired === "true";
        } else if (state.quoteResponse.QuoteExpiryDate) {
          isExpired = isQuoteExpired(state.quoteResponse);
        }
      }

      if (isExpired) {
        // Set isExpired flag to return early from processLoadedQuote(), and display a banner until a new quote is obtained.
        dispatch("setIsExpiredFlag", true);
        dispatch("setIsOriginalQuoteExpired", true);
        // Prevent the customise cover drawer UI being populated by the expired quote.
        dispatch("setIsDrawerCustomiseCoverPopulatedFromApi", true);
      }

      // disable restoring to last step if this is an essential cover quote
      // going from TLI -> TC and vice versa
      if (urlParams.isEssentialCover === "true") {
        dispatch("setRestoringLastStep", false);
      }

      // Load and process biz logic, quotes, prices, activeQuote
      await dispatch("processLoadedQuote");

      // has to be last in line so that we can override some answer
      // with annual requote data that has been saved to the QuoteAndBuyBFF database
      await dispatch("setQuoteAdditionalData");
    } else {
      // 'Create quote' journey
      await dispatch("refreshSession");
      // Create empty quote to avoid default data error after loading json files
      await dispatch("createQuote");

      // Wait to load question set before continuing
      await Promise.all([
        dispatch("loadQuestionsSections"),
        dispatch("application/loadApplicationSections", null, {
          root: true,
        }),
      ]);

      dispatch("processMaxCommissionSacrifice");

      // disable error message when first create quote
      dispatch("setIsLNErrorMessageEnabled", false);
    }
  },
  async redirectToCurrentStage({
    state,
    rootState,
    getters,
    rootGetters,
    dispatch,
  }) {
    const qr = state.quoteResponse;

    if (qr.QuoteStatus === SUBMITTED) {
      const routeName = getters.isSelfServe ? "Success" : "Complete";
      router.push({
        name: routeName,
        params: {
          type: "resumeQuote",
          quoteRequestId: getters.quoteRequestId,
        },
      });
      return;
    }

    if (qr.QuoteStatus === UNABLETOQUOTE) {
      // lastQuoteErrorType will have been set by loadQuote
      // So the error will be there when we land
      const getQuotesStep = rootGetters["steps/getGetQuotesStep"];
      dispatch(
        "steps/handleGoStep",
        getQuotesStep ?? APPLICATION_STEPS.propertyDetails,
        { root: true }
      );
      router.push("/quotation");
      return;
    }

    if (shouldGoToStart(qr)) {
      // QuickQuote or annual reQuote - go to start to confirm answers
      router.push({ name: ROUTE_NAME.HOME.MAIN });
      return;
    }

    if (qr.QuoteStatus === INPROGRESS || qr.QuickQuoteStatus === PROCEEDED) {
      const lastStepEdited = rootState.steps.lastStepEdited;
      if (lastStepEdited) {
        dispatch("steps/handleGoToLastEdited", lastStepEdited, {
          root: true,
        });
        router.push("/quotation");
      } else {
        router.push({ name: ROUTE_NAME.HOME.MAIN });
      }
      return;
    }

    const appStageQuoteId = rootGetters["application/quoteId"];
    if (qr.QuoteStatus === QUOTESRETRIEVED && appStageQuoteId) {
      // Got a quoteId - go application stage
      const applicationStartingStep =
        rootGetters["steps/getApplicationStartingStep"];
      dispatch(
        "steps/handleGoStep",
        applicationStartingStep ?? APPLICATION_STEPS.yourApplication,
        { root: true }
      );
      router.push("/application");
      return;
    }

    dispatch("toggleGAEventFlag");
    // go to quote result stage
    dispatch(
      "steps/handleGoStep",
      getters.getQuoteResultSection?.SectionNumber ??
        APPLICATION_STEPS.yourQuotes,
      { root: true }
    );
    router.push("/quotation");
    return;
  },
  increaseRepeatingQuestionSetIndex({ commit }) {
    commit("INCREASE_REPEATING_QUESTION_SET_INDEX");
  },
  setPropertyData({ commit, getters }, payload) {
    getters.getQuestionLayoutPropertyDetails.Questions.forEach((question) => {
      const matchingValue = payload ? payload[question.InterfaceKey] : null;
      question.Value = matchingValue ? matchingValue : null;
    });
    // Populate lexisNexisResponse even if the response was null
    commit("SET_LEXIS_NEXIS_RESPONSE", payload);
  },
  setPropertyDataAndSources({ commit, state }, payload) {
    if (!payload) return;

    PROPERTY_DETAILS_INTERFACE_KEY.forEach((interfaceKey) => {
      const question = state.questionMap[interfaceKey];
      question.Value =
        payload[question.InterfaceKey] == null
          ? ""
          : payload[question.InterfaceKey];

      question.Source = question.Value ? ANSWER_SOURCES.THIRD_PARTY : "";
    });

    const numberOfBathrooms = state.questionMap["NumberOfBathrooms"];
    const numberOfBedrooms = state.questionMap["NumberOfBedrooms"];

    // If both bathrooms and bedrooms have value, set both their source to ThirdParty
    if (payload["NumberOfBathrooms"] && payload["NumberOfBedrooms"]) {
      numberOfBathrooms.Source = ANSWER_SOURCES.THIRD_PARTY;
      numberOfBedrooms.Source = ANSWER_SOURCES.THIRD_PARTY;
    }

    // If only bathroom or bedroom has value, set both their source to ThirdParty
    if (
      (!payload["NumberOfBathrooms"] && payload["NumberOfBedrooms"]) ||
      (payload["NumberOfBathrooms"] && !payload["NumberOfBedrooms"])
    ) {
      numberOfBathrooms.Source = ANSWER_SOURCES.THIRD_PARTY;
      numberOfBedrooms.Source = ANSWER_SOURCES.THIRD_PARTY;
    }

    // If both bathrooms and bedrooms don't have value, set source to empty
    if (!payload["NumberOfBathrooms"] && !payload["NumberOfBedrooms"]) {
      numberOfBathrooms.Source = "";
      numberOfBedrooms.Source = "";
    }

    commit("SET_LEXIS_NEXIS_RESPONSE", payload);
  },
  setPropertyDetailsFormVisibility({ commit }, payload) {
    commit("SET_PROPERTY_DETAILS_FORM_VISIBILITY", payload);
  },
  setIsLNErrorMessageEnabled({ commit }, payload) {
    commit("SET_IS_LNERROR_MESSAGE_ENABLED", payload);
  },
  setQuestionVisibility({ commit, getters }, question) {
    commit("SET_QUESTION_VISIBILITY", { getters, question });
  },
  updateQuoteResponse({ commit }, data) {
    commit("UPDATE_QUOTERESPONSE", data);
    const systemState = quoteResponseToState(data);
    const quoteAccessId = quoteResponseToQuoteAccessId(data);

    // automation test need quoteRequestId to call API to data res
    sessionStorage.setItem("quoteRequestId", data.QuoteRequestId);

    commit("SET_QUOTE_STATUS", systemState);
    if (quoteAccessId) {
      commit("UPDATE_QUOTEACCESSID", quoteAccessId);
    }
  },
  updateClientErrorResponse({ commit, getters }, data) {
    commit("SET_QUOTE_STATUS", CLIENTERROR);
    commit("UPDATE_CLIENT_ERROR_RESPONSE", data);
    logErrorsToGoogleAnalytics(
      getters.clientErrorMessagesArray,
      getters.productId
    );
  },
  setUserReference({ commit }, payload) {
    commit("SET_USER_REFERENCE", payload);
  },
  handleSaveAndExit({ commit, dispatch }, userReference) {
    commit("SET_USER_REFERENCE", userReference);

    const quoteRequestFields = {
      IsPartialQuote: true,
    };

    return dispatch("updateQuote", quoteRequestFields).then(() => {
      commit("EXIT_TO_ADVISERHUB");
    });
  },
  handleD2CSaveAndExit(
    { state, commit, dispatch, rootGetters },
    exitDestination = EXIT_DESTINATION.d2cHomePage
  ) {
    const quoteRequestFields = {
      IsPartialQuote: true,
    };

    const dateOfBirth = state.questionMap.Applicant1DoB?.Value;
    const postCode = state.questionMap.InsuredAddressPostCode?.Value;

    return dispatch("updateQuote", quoteRequestFields).then(async () => {
      if (dateOfBirth && postCode) {
        try {
          await dispatch(
            "communication/send",
            {
              type: D2C_COM_TYPE.finishQuote,
              quoteReference: rootGetters["questions/quoteReference"],
            },
            {
              root: true,
            }
          );
        } catch (ex) {
          console.error(ex);
        }
      }
      commit(exitDestination);
    });
  },
  saveProgress({ dispatch }) {
    const quoteRequestFields = {
      IsPartialQuote: true,
    };
    dispatch("updateQuote", quoteRequestFields);
  },
  refreshSessionAndNextStep({ dispatch }, path) {
    dispatch("refreshSession");
    router.push({ name: path });
  },
  handleSaveQuote({ dispatch, rootGetters }, insurer) {
    return dispatch("saveQuoteResults")
      .then(() => dispatch("addSelectedQuoteInfo", insurer.selectedQuote))
      .then((selectedQuoteModel) => {
        dispatch(
          "communication/send",
          {
            type: D2C_COM_TYPE.quoteReference,
            quoteReference: rootGetters["questions/quoteReference"],
            selectedQuote: selectedQuoteModel,
          },
          {
            root: true,
          }
        ).catch((err) => console.error(err));
      })
      .catch((e) => console.log(e));
  },
  handleSaveAndExitOnResults({ commit, dispatch }, userReference) {
    commit("SET_USER_REFERENCE", userReference);

    return dispatch("saveQuoteResults")
      .then(() => commit("EXIT_TO_ADVISERHUB"))
      .catch((e) => console.log(e));
  },
  handleD2CSaveAndExitOnResults(
    { commit, dispatch, rootGetters, getters },
    exitDestination = EXIT_DESTINATION.d2cHomePage
  ) {
    return dispatch("saveQuoteResults")
      .then(() => dispatch("addSelectedQuoteInfo", getters["cheapestQuote"]))
      .then((selectedQuoteModel) => {
        dispatch(
          "communication/send",
          {
            type: D2C_COM_TYPE.quoteReference,
            quoteReference: rootGetters["questions/quoteReference"],
            selectedQuote: selectedQuoteModel,
          },
          {
            root: true,
          }
        )
          .catch((err) => console.error(err))
          .finally(() => {
            commit(exitDestination);
          });
      })
      .catch((e) => console.log(e));
  },
  addSelectedQuoteInfo({ rootGetters, getters }, selectedQuote) {
    selectedQuote.ExcessBuildings =
      getters["getNumericAnswer"]("ExcessBuildings");
    selectedQuote.ExcessContents =
      getters["getNumericAnswer"]("ExcessContents");
    selectedQuote.FirstName = getters["getAnswer"]("Applicant1Forename");
    selectedQuote.PaymentMethod = rootGetters["application/allAnswers"].find(
      (ans) => ans.InterfaceKey === "PaymentMethod"
    )?.Value;
    selectedQuote.PaymentFrequency = rootGetters["application/allAnswers"].find(
      (ans) => ans.InterfaceKey === "PaymentFrequency"
    )?.Value;
    return selectedQuote;
  },
  setOverlayVisible({ commit }, onOff) {
    commit("SET_OVERLAY_VISIBLE", onOff);
  },
  exitToAdviserHub({ commit }, destination) {
    commit("EXIT_TO_ADVISERHUB", destination);
  },
  exitToHomePage() {
    router.push({
      name: "LandingPage",
    });
  },
  loadDummyData({ commit, getters }, data) {
    commit("LOAD_RETRIEVED_QUOTE_DATA", { data, getters });
  },
  setCoverAnswers({ commit }, payload) {
    commit("SET_COVER_ANSWERS", payload);
  },
  updateCommissionSacrifice({ commit }, payload) {
    commit("UPDATE_COMMISSION_SACRIFICE", payload);
  },
  setCoverSelection({ commit, getters }, { buildingsAd, contentsAd }) {
    // Remove irrelevant bits (if buildings/contents only)
    const coverTypeQuestion = getters.getQuestionCoverType;
    if (!getters.isContentsOnlyProduct && coverTypeQuestion) {
      if (!includesBuildings(coverTypeQuestion)) {
        buildingsAd = null;
      } else if (!includesContents(coverTypeQuestion)) {
        contentsAd = null;
      }
    }
    commit("SET_COVER_SELECTION", { buildingsAd, contentsAd });
  },
  async loadAnswers({ commit, dispatch, getters }, data) {
    commit("LOAD_RETRIEVED_QUOTE_DATA", { data, getters });
    let lastStepEditedTitle = await dispatch("getActualLastStepEdited");
    commit("SET_LAST_STEP_EDITED", lastStepEditedTitle);
  },
  loadQuote({ getters, dispatch, commit }, quoteRequestId) {
    const config = getters.httpConfig;
    return quoteService
      .retrieveQuote(quoteRequestId, config)
      .then(async (data) => {
        // Store the quoteResponse
        await dispatch("updateQuoteResponse", data);

        // If applicable, store as the applyResponse too
        if (data.QuoteStatus === "SUBMITTED") {
          await dispatch("application/updateApplyResponse", data, {
            root: true,
          });
        }
        return data;
      })
      .catch((error) => {
        commit("SET_QUOTE_STATUS", FAILED);
        throw error; // Rethrow for downstream handlers
      });
  },
  loadQuoteByAccessId({ getters, dispatch, commit }, quoteAccessId) {
    const config = getters.httpConfig;
    return quoteService
      .retrieveQuoteByAccessId(quoteAccessId, config)
      .then(async (data) => {
        // Store the quoteResponse
        await dispatch("updateQuoteResponse", data);

        // If applicable, store as the applyResponse too
        if (data.QuoteStatus === "SUBMITTED") {
          await dispatch("application/updateApplyResponse", data, {
            root: true,
          });
        }
        return data;
      })
      .catch((error) => {
        commit("SET_QUOTE_STATUS", FAILED);
        throw error; // Rethrow for downstream handlers
      });
  },
  async processLoadedQuote({ state, getters, commit, dispatch }) {
    // Called once we have loaded everything:
    // Catalogue, Products, QuestionSet, and the QuoteResponse (if resuming)

    // Short name for ease
    const qr = state.quoteResponse;

    const isQuickQuote = qr.QuickQuoteContinuationUri > "" || qr.IsQuickQuote;
    commit("SET_QUICK_QUOTE", isQuickQuote);

    // Update CommSac early, before pricing (if this is not an expired quote that is being requoted)
    if (qr.CommissionSacrifice && !getters.isExpired)
      commit("UPDATE_COMMISSION_SACRIFICE", qr.CommissionSacrifice);

    if (qr.UserReference) commit("SET_USER_REFERENCE", qr.UserReference);

    // Populate the answers
    await dispatch("loadAnswers", qr.Answers);

    if (
      isQuickQuote &&
      qr.QuickQuoteStatus &&
      (qr.QuickQuoteStatus === QUICKQUOTERETRIEVED ||
        qr.QuickQuoteStatus === PROCEEDED)
    ) {
      // INFO: skip restoring cover answer data for d2c landlord because it uses data straight from questionMap
      // we'll see if this way is better than splitting cover answers into a different vuex state
      // and will refactor other journey to use the most optimal way later
      if (!(getters.isSelfServe && getters.productId == PRODUCT.LandLord)) {
        const quickQuoteCoverAnswers = qr.Answers.filter(
          (t) =>
            t.InterfaceKey === "HomeEmergency" ||
            t.InterfaceKey === "LegalExpenses" ||
            t.InterfaceKey === "BuildingsCoverLevel" ||
            t.InterfaceKey === "ExcessBuildings" ||
            t.InterfaceKey === "ContentsCoverLevel" ||
            t.InterfaceKey === "ExcessContents"
        );
        await dispatch("setCoverAnswers", quickQuoteCoverAnswers);
      }
      await dispatch("setOriginalQuickQuoteProperyData");

      // always set to false if the quick quote has not confirm eligibility on Home page
      // so that we can display popup confirm eligibility
      const homeEligibilityAnswer = qr.Answers.find(
        (x) => x.InterfaceKey === "HomeEligibility"
      );
      const hasConfirmedHomeEligibility =
        hasConfirmedEligibility(homeEligibilityAnswer) ||
        qr.QuoteAdditionalData?.IsEligibilityConfirmed === true
          ? "True"
          : "False";

      await dispatch("setHomeEligibility", hasConfirmedHomeEligibility);
    } else {
      const quoteCoverAnswers = state.quoteResponse.Answers.filter((answer) =>
        CUSTOMISE_COVER_INTERFACE_KEYS.includes(answer.InterfaceKey)
      );
      await dispatch("setCoverAnswers", quoteCoverAnswers);
      await dispatch("setHomeEligibility", qr);
    }

    // Restore first time buyer NCD state
    await dispatch("restoreFirstTimeBuyerNcdState", qr.Answers);

    // We don't need to restore anything past Property details if an expired quote is being requoted
    if (getters.isExpired) return;

    await dispatch("application/loadAnswers", qr.Answers, {
      root: true,
    });

    // Load Cover (AD) after CoverType Answer has loaded
    if (qr.CoverSelections)
      await dispatch("restoreCoverSelections", qr.CoverSelections);

    // Restore SelectedForComparison quotes if applicable
    if (getters.canSelectForComparison && qr.Quotes?.length) {
      const selectedQuoteIds = qr.Quotes.filter(
        (q) => q.SelectedForComparison
      ).map((q) => q.QuoteId);
      dispatch("setSelectedQuoteIds", selectedQuoteIds);
    }

    // insurerQuotesModel is now available as it relies on:
    // QuoteResponse, CoverSelection, and CommissionSacrifice
    // Set activeQuote
    const activeQuote = getters.activeQuote;
    if (activeQuote) {
      dispatch("application/setApplicationActiveQuote", activeQuote, {
        root: true,
      });
    }
  },
  processMaxCommissionSacrifice({ state, getters, commit }) {
    if (!state.products || state.products.length === 0) return;
    const comSacModel = {
      branchNumber: getters.branchNumber,
      productsData: [...state.products],
    };
    commit("SET_MAX_COMMISSION_SACRIFICE", comSacModel);
  },
  setIsDrawerCustomiseCoverPopulatedFromApi({ commit }, data) {
    commit("SET_IS_DRAWER_CUSTOMISE_COVER_POPULTED_FROM_API", data);
  },
  setFirstTimeBuyerNcd({ dispatch }, question) {
    const value = getDirectOrDeepValue(question);
    // When OwnershipProperty is changing:
    // If the answer is now NOT in OwnedMortgaged or SharedOwnersip,
    // change any ncdState member that is currently DEFAULTED to CLEAN,
    // and change the matching NCD answer value to "" empty string.
    if (
      question.InterfaceKey === "OwnershipProperty" &&
      value !== "OwnedMortgaged" &&
      value !== "SharedOwnership"
    ) {
      dispatch("clearFirstTimeBuyerNcd");
    }

    // When ResidentialStatus is changing:
    if (question.InterfaceKey === "ResidentialStatus") {
      // If the answer is now FirstTimeBuyer:
      // change any ncdState member that is currently CLEAN to DEFAULTED,
      // and change the matching NCD answer value to 2.
      if (value === "FirstTimeBuyer") {
        dispatch("defaultFirstTimeBuyerNcd");
      }
      // If the answer is now NOT FirstTimeBuyer:
      // change any ncdState member that is currently DEFAULTED to CLEAN,
      // and change the matching NCD answer value to "" empty string.
      if (value !== "FirstTimeBuyer") {
        dispatch("clearFirstTimeBuyerNcd");
      }
    }
  },
  defaultFirstTimeBuyerNcd({ state, commit }) {
    if (state.ncdBuildingsState === CLEAN) {
      commit("SET_NCD_BUILDINGS_STATE", DEFAULTED);

      state.questionMap.NoClaimsBuildings.Value = FIRST_TIME_BUYER_NCD;
    }
    if (state.ncdContentsState === CLEAN) {
      commit("SET_NCD_CONTENTS_STATE", DEFAULTED);

      state.questionMap.NoClaimsContents.Value = FIRST_TIME_BUYER_NCD;
    }
  },
  clearFirstTimeBuyerNcd({ state, commit }) {
    if (state.ncdBuildingsState === DEFAULTED) {
      commit("SET_NCD_BUILDINGS_STATE", CLEAN);

      state.questionMap.NoClaimsBuildings.Value = "";
    }
    if (state.ncdContentsState === DEFAULTED) {
      commit("SET_NCD_CONTENTS_STATE", CLEAN);

      state.questionMap.NoClaimsContents.Value = "";
    }
  },
  restoreFirstTimeBuyerNcdState({ state, commit }, answers) {
    const residentialStatus = answers.find(
      (a) => a.InterfaceKey === "ResidentialStatus"
    );
    if (residentialStatus?.Value === "FirstTimeBuyer") {
      // Restoring ncdBuildingsState to DEFAULTED ensures that the buildings
      // NCD radio buttons are disabled for resumed FirstTimeBuyer quotes.
      commit("SET_NCD_BUILDINGS_STATE", DEFAULTED);

      state.questionMap.NoClaimsBuildings.Value = FIRST_TIME_BUYER_NCD;

      // Establish whether or not the user changed the ncdContents value
      // from the default 2 years and update ncdContentsState accordingly.
      const ncdContents = answers.find(
        (a) => a.InterfaceKey === "NoClaimsContents"
      );
      if (ncdContents?.Value === "Two") {
        commit("SET_NCD_CONTENTS_STATE", DEFAULTED);
      }
    }
  },
  restoreCoverSelections({ dispatch }, cover) {
    // cover is API CoverSelections
    const buildingsAd = adStringToBool(cover.BuildingsAD);
    const contentsAd = adStringToBool(cover.ContentsAD);
    dispatch("setCoverSelection", { buildingsAd, contentsAd });
  },
  submitFactFind({ commit }, form) {
    commit("SUBMIT_FACT_FIND", form);
  },
  getActualLastStepEdited({ state, getters, rootGetters }) {
    let lastStepEdited = state.lastEditedSectionTitle;
    const cover = getters.getCover;
    // redirect to current last step edited if
    // 1. quote is not referral self serve
    // 2. cover data is null
    // 3. and quote is in state INPROGRESS
    if (
      state.quoteResponse.CallChannelType !== CALL_CHANNEL.referralSelfServe ||
      (state.quoteResponse.QuoteStatus !== INPROGRESS &&
        (!state.quoteResponse.QuickQuoteStatus ||
          state.quoteResponse.QuickQuoteStatus !== PROCEEDED)) ||
      !cover
    ) {
      return lastStepEdited;
    }
    // get last step section to get its section number
    const lastStepSection = state.form.sections.find(
      (x) => x.SectionTitle === state.lastEditedSectionTitle
    );
    const getQuoteStepSectionNumber = rootGetters["steps/getGetQuotesStep"];
    const getQuoteStepSectionNumberTitle = state.form.sections.find(
      (x) => x.SectionNumber === getQuoteStepSectionNumber
    )?.SectionTitle;
    // redirect to current last step edited if
    // 1. can't find last step section
    // 2. last step section number > get quote step section number
    if (
      !lastStepSection ||
      lastStepSection.SectionNumber > getQuoteStepSectionNumber
    ) {
      return lastStepEdited;
    }

    if (
      lastStepEdited === STEP_TITLE.propertyDetails &&
      getters.hasPropertyDetail
    ) {
      // Set current step to the Get Quotes step (before results)
      lastStepEdited = getQuoteStepSectionNumberTitle;
    }

    return lastStepEdited;
  },
  setIsExpiredFlag({ commit, dispatch }, data) {
    commit("SET_IS_EXPIRED_FLAG", data);
    // set restoring last step to false so that when we redirect
    // to step 1 we don't try to go to next step
    // required for every journey except for Home
    if (data === true) {
      dispatch("setRestoringLastStep", false);
    }
  },
  getAppConfigurationContactInfo({ commit, getters }) {
    const config = getters.httpConfig;
    return appConfigurationService
      .getAppConfigurationByType(config, "ContactInfo")
      .then((data) => {
        commit("SET_CONTACT_INFO", data);
      })
      .catch((error) => console.log(error));
  },
  setReplacementProductId({ commit, getters }) {
    const replacementProductId = getters.replacementProductId;
    commit("UPDATE_PRODUCT_ID", replacementProductId);
  },
  setProductTransferInProgressFlag({ commit }, data) {
    commit("SET_PRODUCT_TRANSFER_IN_PROGRESS_FLAG", data);
  },
  syncQuoteRequest({ state, getters }) {
    const config = getters.httpConfig;
    return quoteService
      .syncQuoteRequest(state.quoteResponse, config)
      .catch((err) => {
        console.error(err);
      });
  },
  setRefreshQuote({ commit }, data) {
    commit("SET_REFRESH_QUOTE", data);
  },
  setHomeEligibility({ commit }, data) {
    commit("SET_HOME_ELIGIBILITY", data);
  },

  setQuoteAdditionalData({ state }) {
    const quoteResponse = state.quoteResponse;

    state.quoteAdditionalData = quoteResponse.QuoteAdditionalData ?? {};
    const isAnnualRequote = state.quoteAdditionalData.IsAnnualRequote;

    // see if this quote is annual requote, if true then we check whether we have confirmed annual requote
    if (isAnnualRequote) {
      state.isAnnualRequote = true;
    }

    if (
      state.quoteAdditionalData &&
      state.quoteAdditionalData.IsEligibilityConfirmed === true
    ) {
      if (state.questionMap.InsuredParty) {
        state.questionMap.InsuredParty.Value =
          state.quoteAdditionalData.InsuredParty;
      }
      if (state.questionMap.CompanyName) {
        state.questionMap.CompanyName.Value =
          state.quoteAdditionalData.CompanyName;
      }
    }
  },
  setQuoteRequestIdAfterRetrieve({ commit }, data) {
    commit("SET_IS_QUOTE_REQUEST_ID_AFTER_RETRIEVE", data);
  },
  setSelectedQuoteIds({ commit }, data) {
    commit("SET_SELECTED_QUOTE_IDS", data);
  },
  async createTransferQuote({ dispatch }, payload) {
    // Reset/clear cover answers in state (Excess, LE, HE, PP's, etc.)
    dispatch("setCoverAnswers", []);

    if (payload.isCreateQuoteFromTransferDialog) {
      // Set productTransferInProgress flag so that UI items can be reset in the DrawerCustomiseCover.vue
      dispatch("setProductTransferInProgressFlag", true);
    } else {
      // Reset/clear AD values:
      dispatch("setCoverSelection", {
        buildingsAd: false,
        contentsAd: false,
      });
    }

    // Reset/clear commission sacrifice (in state)
    dispatch("updateCommissionSacrifice", 0);

    // Set ProductId equal to the ReplacementProductId
    dispatch("setReplacementProductId");

    const quoteRequestFields = {
      IsPartialQuote: payload.isPartialQuote,
    };

    await dispatch("getQuotes", quoteRequestFields)
      .then((res) => {
        if (!payload.isCreateQuoteFromTransferDialog) {
          sessionStorage.setItem("isNewQuoteFromExpiredQuote", true);
        }
        window.onbeforeunload = null; //to remove the additional popup of "Leave Site"
        window.location.href = res.QuoteRequestContinuationUri;
      })
      .catch((error) => {
        console.log("refreshCover error", error);
      });
  },
  toggleSaveAndExitDialog({ commit }) {
    commit("TOGGLE_SAVE_AND_EXIT_DIALOG");
  },
  toggleGlobalSaveAndExitDialog(
    { commit },
    exitDestination = EXIT_DESTINATION.d2cHomePage
  ) {
    commit("SET_EXIT_DESTINATION", exitDestination);
    commit("TOGGLE_GLOBAL_SAVE_AND_EXIT_DIALOG");
  },
  toggleQuoteExpiredDialog({ state }, data) {
    state.isShowQuoteExpiredDialog = data;
  },
  async saveAndExit({ state, dispatch, getters }, payload) {
    var observer = state.currentRefObserverForm;

    if (observer) {
      await observer.validate({ silent: true });
    }

    dispatch("toggleGlobalSaveAndExitDialog", state.exitDestination);
    dispatch("toggleAppOverlay", true);

    // D2C and B2B has difference api call save and exit
    if (getters.isSelfServe) {
      dispatch("journeySaveAndExit", {
        saveAndExitOnResults: "handleD2CSaveAndExitOnResults",
        saveAndExitQuestions: "handleD2CSaveAndExit",
        saveAndExitApplication: "application/handleD2CSaveAndExit",
        payload: state.exitDestination,
      });
    } else {
      dispatch("journeySaveAndExit", {
        saveAndExitOnResults: "handleSaveAndExitOnResults",
        saveAndExitQuestions: "handleSaveAndExit",
        saveAndExitApplication: "application/handleSaveAndExit",
        payload: payload,
      });
    }
  },

  // in each stage of a journey, we have different saveAndExit api call
  // check current stage and call the corresponds api
  journeySaveAndExit(
    { dispatch, getters },
    {
      saveAndExitOnResults,
      saveAndExitQuestions,
      saveAndExitApplication,
      payload,
    }
  ) {
    let saveFunc;
    let root = false;
    if (getters.isQuoteResultPage) {
      saveFunc = saveAndExitOnResults;
    } else if (getters.isQuestionSteps) {
      saveFunc = saveAndExitQuestions;
    } else {
      saveFunc = saveAndExitApplication;
      root = true;
    }
    dispatch(saveFunc, payload, { root: root }).finally(() => {
      dispatch("toggleAppOverlay", false);
    });
  },
  toggleAppOverlay({ commit }, payload) {
    commit("SET_SHOW_APP_OVERLAY", payload);
  },
  setQuestionMap({ commit, getters }) {
    const questionMap = getters.allQuestions.reduce((questionMap, question) => {
      if (question.InterfaceKey) {
        questionMap[question.InterfaceKey] = question;
      }
      return questionMap;
    }, {});
    commit("SET_QUESTION_MAP", questionMap);
  },
  /**
   * Retrieves the D2C quote document.
   *
   * @param {Object} getters - The getters object from the Vuex store.
   * @returns {Promise<Object>} A promise that resolves to the quote documents map using document code as key.
   * @throws {Error} If there is an error while retrieving the quote document.
   */
  async getD2CQuoteDocument({ getters }) {
    const res = await documentService.getDocumentList(
      getters.getQuoteRequestId,
      getters.callChannel,
      getters.httpConfig
    );
    if (res.StatusCode !== "OK") {
      return {};
    }
    const documents = {};

    res.Documents.forEach((doc) => {
      doc.isDownloading = false;
      doc.isDownloaded = false;
      if (doc.Code === "terms" || doc.Code === "ipid") {
        documents[doc.Code] = doc;
      }
    });

    return documents;
  },
  setRestoringLastStep({ commit }, data) {
    commit("SET_RESTORING_LAST_STEP", data);
  },
  toggleGAEventFlag({ commit }) {
    commit("TOGGLE_GA_EVENT_FLAG");
  },
  setCompanyName({ state }, data) {
    state.companyName = data;
  },
  setInsuredParty({ state }, data) {
    state.insuredParty = data;
  },
  async confirmEligibility({ state }, quoteAccessId) {
    return quoteService.confirmEligibility({
      quoteAccessId,
      insuredParty: state.insuredParty,
      companyName: state.companyName,
      isExpiredQuoteConfirmed: state.isOriginalQuoteExpired,
    });
  },
  updateHasResetQuickQuotePropertyData({ commit }, value) {
    commit("HAS_RESET_QUICKQUOTE_PROPERTYDATA", value);
  },
  setOriginalQuickQuoteProperyData({ state }) {
    state.form.originalQuickQuotePropertyData = {};
    if (!state.questionMap) {
      return;
    }
    PROPERTY_LOOKUP.forEach((interfaceKey) => {
      if (
        state.questionMap[interfaceKey] &&
        state.questionMap[interfaceKey].Value &&
        state.questionMap[interfaceKey].Source?.toLowerCase() == "user"
      ) {
        state.form.originalQuickQuotePropertyData[interfaceKey] =
          state.questionMap[interfaceKey].Value;
      }
    });
  },
  async getDocuments({ getters }) {
    var res = await documentService.getDocumentList(
      getters.getQuoteRequestId,
      getters.callChannel,
      getters.httpConfig
    );

    if (res.StatusCode !== "OK") {
      return [];
    }

    //The code below is to have a copy of these files for each quote so we could track their download state.
    //only two records of ipid and terms are coming back from api, we need one for each quote for their download state.
    let termFile;
    let ipidFile;
    let factfindsdn;

    res.Documents.forEach((doc) => {
      doc.isDownloading = false;
      doc.isDownloaded = false;

      if (doc.Code === "terms") {
        termFile = doc;
      } else if (doc.Code === "ipid") {
        ipidFile = doc;
      } else if (doc.Code === "factfindsdn") {
        factfindsdn = doc;
      }
    });

    getters.insurerQuotesModel.forEach((quote) => {
      if (!quote.isAvailable) {
        return;
      }
      quote.quotes.forEach((insurerQuote) => {
        if (termFile) {
          res.Documents.push({
            ...termFile,
            QuoteId: insurerQuote.QuoteId,
          });
        }
        if (ipidFile) {
          res.Documents.push({
            ...ipidFile,
            QuoteId: insurerQuote.QuoteId,
          });
        }
        if (factfindsdn) {
          res.Documents.push({
            ...factfindsdn,
            QuoteId: insurerQuote.QuoteId,
          });
        }
      });
    });

    return res.Documents;
  },
  getDataToPrintAllQuotes({ getters }, { docs, ads }) {
    return {
      type: "multi",
      selectedAds: ads,
      files: docs.filter((doc) => filterMultiQuoteDocs(doc, ads)).reverse(),
      quoteRequestId: getters.getQuoteRequestId,
    };
  },
  getDataToPrintQuote(_, { docs, quote, paymentFrequency }) {
    return {
      ...quote,
      files: docs.filter((doc) =>
        filterSingleQuoteDocs(doc, quote.QuoteId, paymentFrequency)
      ),
      type: "single",
    };
  },
  resetInvalidQuestions({ commit }, errors) {
    commit("RESET_INVALID_QUESTIONS", { errors });
  },
  async fetchQuoteExcessConfiguration({ commit, getters, state }) {
    if (!isEmpty(state.quoteExcessConfiguration)) {
      return;
    }
    try {
      const config = getters.httpConfig;
      const data = await appConfigurationService.getAppConfigurationByType(
        config,
        "QuoteExcess"
      );
      commit("SET_QUOTE_EXCESS_CONFIGURATION", data);
    } catch (error) {
      console.log(error);
      commit("SET_QUOTE_EXCESS_CONFIGURATION", null);
    }
  },
  setIsOriginalQuoteExpired({ state }, data) {
    state.isOriginalQuoteExpired = data;
  },
  resetFactFindSecondApplicantFields({ commit, getters }) {
    if (isQuilterSpecific(getters.qsId)) {
      commit("RESET_FACT_FIND_SECOND_APPLICANT", "landlordApplicant2");
    }
  },
  async applyForEssentialCover({ dispatch, getters }, productId) {
    try {
      dispatch("setOverlayVisible", true);

      const quoteRequest = {
        ...getters.getQuoteRequest,
        ProductId: productId,
      };

      const response = await quoteService.createQuote(
        quoteRequest,
        getters.httpConfig
      );

      const uri = new URL(response.QuoteRequestContinuationUri);
      uri.searchParams.append("isEssentialCover", true);

      window.open(uri.toString(), "_blank");
    } catch (error) {
      console.log(error);
    } finally {
      dispatch("setOverlayVisible", false);
    }
  },
  setCurrentRefObserverForm({ commit }, form) {
    commit("SET_CURRENT_REF_OBSERVER_FORM", form);
  },
  setCurrentPath({ commit }, path) {
    commit("SET_CURRENT_PATH", path);
  },
  setProductId({ commit }, productId) {
    commit("UPDATE_PRODUCT_ID", productId);
  },
  setConfirmationRentCover({ commit }, value) {
    commit("SET_RENT_COVER_CONFIRMATION", value);
  },
};
